import type {
  Audit,
  PaginatedList,
  PaginationData,
  Session,
} from '@local/common-lib';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  Pagination,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { ErrorControl } from '../../components/error/index.js';
import { HorizontalMiddleSpinner } from '../../components/middle-spinner/index.js';
import { CenterLayout } from '../../components/page-layout/index.js';
import type { UseAxiosResult } from '../../hooks/use-axios.js';
import { useAxios } from '../../hooks/use-axios.js';
import { useSetTitle } from '../../hooks/use-title.js';
import { useAuth, useAuthenticated } from '../../utils/auth/index.js';
import { NarrowAuditList, WideAuditList } from './audit-list.js';
import { SessionList } from './session-list.js';

interface AuditsViewProps {
  auditPagination: PaginationData;
  audits: UseAxiosResult<PaginatedList<Audit.Type>>;
  setAuditPagination: (pagination: PaginationData) => void;
}

interface SessionsViewProps {
  revoke: (session: Session.Type) => unknown;
  revoking: boolean;
  sessionPagination: PaginationData;
  sessions: UseAxiosResult<PaginatedList<Session.Type>>;
  setSessionPagination: (pagination: PaginationData) => void;
}

interface ViewProps extends AuditsViewProps, SessionsViewProps {}

export const SecurityPage: FC<{ view?: FC<ViewProps> }> = ({
  view: View = SecurityView,
}) => {
  const auth = useAuth();
  const { axios } = useAuthenticated();
  const { enqueueSnackbar } = useSnackbar();
  const [revoking, setRevoking] = useState(false);
  useSetTitle('Security');

  const [sessionPagination, setSessionPagination] = useState({
    limit: 12,
    skip: 0,
  });
  const [sessions, refreshSessions] = useAxios<PaginatedList<Session.Type>>(
    '/api/v1/my/sessions',
    useMemo(
      () => ({ params: { ...sessionPagination, sort: '-refreshed' } }),
      [sessionPagination],
    ),
  );

  const [auditPagination, setAuditPagination] = useState({
    limit: 12,
    skip: 0,
  });
  const [audits] = useAxios<PaginatedList<Audit.Type>>(
    '/api/v1/my/audits',
    useMemo(
      () => ({ params: { ...auditPagination, sort: '-when' } }),
      [auditPagination],
    ),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const revoke = useCallback((session: Session.Type) => {
    setRevoking(true);
    axios
      .post(`/api/v1/my/session/${encodeURIComponent(session._id)}/revoke`)
      .then(() => {
        setRevoking(false);
        enqueueSnackbar('Session revoked', { variant: 'success' });
        refreshSessions();
        return auth.refresh();
      })
      .catch((error) => {
        console.log(error);
        Sentry.captureException(error);
        setRevoking(false);
        enqueueSnackbar('Error revoking session', { variant: 'error' });
        return auth.refresh();
      });
  }, []);

  return (
    <View
      auditPagination={auditPagination}
      audits={audits}
      revoke={revoke}
      revoking={revoking}
      sessionPagination={sessionPagination}
      sessions={sessions}
      setAuditPagination={setAuditPagination}
      setSessionPagination={setSessionPagination}
    />
  );
};

export const AuditContent: FC<AuditsViewProps> = ({
  audits,
  auditPagination,
  setAuditPagination,
}) => {
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('md'));

  if (audits.loading) {
    return <HorizontalMiddleSpinner />;
  }

  if (audits.error) {
    return <ErrorControl error={audits.error} />;
  }

  return (
    <>
      {audits.data.matches ? (
        smallScreen ? (
          <NarrowAuditList audits={audits.data} />
        ) : (
          <WideAuditList audits={audits.data} />
        )
      ) : (
        <CardContent>
          <Typography sx={{ color: 'GrayText', fontStyle: 'italic' }}>
            No sessions
          </Typography>
        </CardContent>
      )}

      {audits.data.matches > auditPagination.limit ? (
        <Pagination
          count={Math.ceil(audits.data.matches / auditPagination.limit)}
          onChange={(_, page) => {
            setAuditPagination({
              limit: auditPagination.limit,
              skip: auditPagination.limit * (page - 1),
            });
          }}
          page={Math.floor(auditPagination.skip / auditPagination.limit) + 1}
          showFirstButton
          showLastButton
          sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
        />
      ) : undefined}
    </>
  );
};

export const SessionContent: FC<SessionsViewProps> = ({
  revoke,
  revoking,
  sessions,
  sessionPagination,
  setSessionPagination,
}) => {
  if (sessions.loading) {
    return <HorizontalMiddleSpinner />;
  }

  if (sessions.error) {
    return <ErrorControl error={sessions.error} />;
  }

  return (
    <>
      {sessions.data.matches ? (
        <SessionList
          revoke={revoke}
          revoking={revoking}
          sessions={sessions.data}
        />
      ) : (
        <CardContent>
          <Typography sx={{ color: 'GrayText', fontStyle: 'italic' }}>
            No sessions
          </Typography>
        </CardContent>
      )}

      {sessions.data.matches > sessionPagination.limit ? (
        <Pagination
          count={Math.ceil(sessions.data.matches / sessionPagination.limit)}
          onChange={(_, page) => {
            setSessionPagination({
              limit: sessionPagination.limit,
              skip: sessionPagination.limit * (page - 1),
            });
          }}
          page={
            Math.floor(sessionPagination.skip / sessionPagination.limit) + 1
          }
          showFirstButton
          showLastButton
          sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
        />
      ) : undefined}
    </>
  );
};

export const SecurityView: FC<ViewProps> = ({
  audits,
  auditPagination,
  revoke,
  revoking,
  sessions,
  sessionPagination,
  setAuditPagination,
  setSessionPagination,
}) => {
  return (
    <CenterLayout>
      <Grid container spacing={3}>
        <Grid item md={12} sx={{ flexGrow: 1 }}>
          <Stack direction="column" spacing={3}>
            <Card>
              <CardHeader title="Current Sessions" />
              <SessionContent
                revoke={revoke}
                revoking={revoking}
                sessionPagination={sessionPagination}
                sessions={sessions}
                setSessionPagination={setSessionPagination}
              />
            </Card>
            <Card>
              <CardHeader title="Audit History" />
              <AuditContent
                auditPagination={auditPagination}
                audits={audits}
                setAuditPagination={setAuditPagination}
              />
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </CenterLayout>
  );
};
